import { positionActions, positionSelector } from '../store/reducers/positionSlice/positionSlice';
import { charterActions } from '../store/reducers/charterSlice/charterSlice';
import { useDispatch, useSelector } from 'react-redux';
import { stageSelector } from '../store/reducers/stageSlice/stageSlice';
import moment from 'moment/moment';
import { useUpdateConnectionPositions } from './useUpdateConnectionPositions';
import { getCountDaysFromDate, getUpdatePositions } from '../helpers/getCountDays';
import { getLongPositions } from '../helpers/getLongPositions';
import { getChains } from '../helpers/getChains';
import { updateChain } from '../helpers/updateChain';
import { updatePositionWithoutConnect } from '../helpers/updatePositionWithoutConnect';
import { useUpdateAll } from './useUpdateAll';
import { createWaterfall } from '../helpers/createWaterfall';
import { getChainConnection } from '../helpers/getChainConnection';
import { queueFetch } from '../helpers/queueFetch';
import { getConnectionsFromOtherItem } from '../helpers/getConnectionsFromOtherItem';
import { gantActions } from '../store/reducers/gantSlice/gantSlice';
import { removeManyConnections } from '../store/reducers/positionsConnections/actions/deleteManyConnections';
import { positionsConnectionsActions } from '../store/reducers/positionsConnections/positionsConnectionsSlice';

export const useEditCharter = (charter) => {
    const dispatch = useDispatch();
    const allPositions = useSelector(positionSelector.selectEntities);
    const positionsEntities = useSelector(positionSelector.selectEntities);
    const allStage = useSelector(stageSelector.selectAll);

    const parentStage = allStage.find((stage) => stage.positions.includes(charter.guid));

    const { updateAll } = useUpdateAll(parentStage);

    const { updateConnectionPositions } = useUpdateConnectionPositions();
    const lastLaborPositionGuid = charter.children
        .filter((guid) => allPositions[guid].type === 'labor')
        .filter((guid) => allPositions[guid].planned.days_count)
        .sort(
            (positionA, positionB) =>
                moment(allPositions[positionA].planned.end_date).valueOf() -
                moment(allPositions[positionB].planned.end_date).valueOf()
        )
        .slice(-1)[0];
    const { settings } = useSelector((state) => state.gantReducer);

    const laborCount = charter.children.filter(
        (positionGuid) => allPositions[positionGuid].type === 'labor'
    ).length;

    const charterPositions = charter.children.map((guid) => allPositions[guid]);

    const positionsWithConnection = charterPositions
        .filter((position) => !!position.connections.length)
        .sort(
            (positionA, positionB) =>
                moment(positionA.planned.end_date).valueOf() -
                moment(positionB.planned.end_date).valueOf()
        );

    const onHidePopup = () => dispatch(gantActions.changeShowPopup(false));

    const resetCharter = (charter) => {
        const resetPlanned = {
            days_count: null,
            start_date: null,
            end_date: null,
        };

        const queue = [];
        const connections = [];

        const resetConnection = [];

        let resultPositionsEntities = { ...allPositions };
        charter.children.forEach((positionGuid) => {
            const currentPosition = resultPositionsEntities[positionGuid];

            currentPosition.connections.forEach((connection) => {
                const isRepeatedPosition = connections.some(
                    (connectionResult) =>
                        connectionResult.positionFromGuid === connection.from &&
                        connectionResult.positionToGuid === connection.to
                );
                if (!isRepeatedPosition) {
                    connections.push({
                        positionFromGuid: connection.from,
                        positionToGuid: connection.to,
                        connectionType: connection.type,
                    });
                }
            });

            if (currentPosition.connections.length) {
                currentPosition.connections.forEach((connection) => {
                    if (connection.to === currentPosition.guid) {
                        const connectionsFrom = resultPositionsEntities[
                            connection.from
                        ].connections.filter((connectionFrom) => {
                            return connectionFrom.to !== connection.to;
                        });

                        resultPositionsEntities[connection.from] = {
                            ...resultPositionsEntities[connection.from],
                            connections: connectionsFrom,
                        };
                    } else {
                        const connectionsTo = resultPositionsEntities[
                            connection.to
                        ].connections.filter(
                            (connectionTo) => connectionTo.from !== connection.from
                        );

                        resultPositionsEntities[connection.to] = {
                            ...resultPositionsEntities[connection.to],
                            connections: connectionsTo,
                        };
                    }
                });
            }

            const updatedPosition = {
                ...resultPositionsEntities[positionGuid],
                planned: resetPlanned,
                connections: resetConnection,
            };

            queue.push({
                data: {
                    data: {
                        guid: updatedPosition.guid,
                        startDate: null,
                        endDate: null,
                    },
                    type: 'update',
                    action: 'reset_group',
                },
                subEntityType: 'step',
                subEntityId: parentStage.id,
                t: Date.now(),
            });

            resultPositionsEntities[positionGuid] = updatedPosition;
        });

        const queueConnections = [
            {
                data: {
                    data: {
                        connection: connections,
                    },
                    type: 'remove_connections',
                    action: 'reset_group',
                },
                subEntityType: 'step',
                subEntityId: parentStage.id,
                t: Date.now(),
            },
        ];

        const removedConnections = connections.map((connection) => ({
            from: connection.positionFromGuid,
            to: connection.positionToGuid,
            type: connection.connectionType,
        }));

        queueFetch(queueConnections);
        queueFetch(queue);

        updateAll(resultPositionsEntities);

        dispatch(
            charterActions.updateOne({
                id: charter.guid,
                changes: { ...charter, planned: resetPlanned },
            })
        );

        dispatch(removeManyConnections(removedConnections));
    };

    const crateCharter = (charter, charterForm) => {
        let planned;

        const queue = [];

        const charterPositions = charter.children.map((guid) => allPositions[guid]);
        const charterLabor = charterPositions.filter((position) => position.type === 'labor');
        const charterMHD = charterPositions.filter((position) => position.type !== 'labor');

        const lastLaborPosition = charterLabor
            .sort(
                (positionA, positionB) =>
                    moment(positionA.planned.end_date).valueOf() -
                    moment(positionB.planned.end_date).valueOf()
            )
            .slice(-1)[0];

        if (!charterLabor.length) {
            let updatedPositionsEntities = {};

            const start_date = charterForm.date
                ? charterForm.date
                : moment().hour(0).minute(0).seconds(0).format();

            const end_date = moment(start_date)
                .add(+charterForm.days - 1, 'days')
                .format();

            charterMHD.forEach((position) => {
                queue.push({
                    data: {
                        data: {
                            guid: position.guid,
                            startDate: moment(start_date).format('YYYY-MM-DD'),
                            endDate: moment(end_date).format('YYYY-MM-DD'),
                        },
                        type: 'update',
                        action: 'create_group',
                    },
                    subEntityType: 'step',
                    subEntityId: parentStage.id,
                    t: Date.now(),
                });
                updatedPositionsEntities[position.guid] = {
                    ...position,
                    planned: {
                        start_date,
                        end_date,
                        days_count: charterForm.days,
                    },
                };
            });

            updateAll(updatedPositionsEntities);
            queueFetch(queue);
            return;
        }

        if (Number(charterForm.days) < charterLabor.length) {
            dispatch(gantActions.changeShowPopup(true));
        }

        const resultDays =
            Number(charterForm.days) < laborCount ? `${laborCount}` : charterForm.days;

        let currentDate = !!charterForm.date
            ? charterForm.date
            : moment().hour(0).minute(0).seconds(0).format();

        planned = {
            days_count: resultDays,
            start_date: currentDate,
            end_date: moment()
                .hour(0)
                .minute(0)
                .seconds(0)
                .add(Number(resultDays) - 1, 'days')
                .format(),
        };

        const updatedCharter = { ...charter, planned };

        const { long, remainder } = getLongPositions(laborCount, +resultDays);

        let mhdDaysCount =
            Number(updatedCharter.planned.days_count) < +settings.daysMHD
                ? Number(updatedCharter.planned.days_count)
                : settings.daysMHD;

        const plannedMhd = {
            start_date: currentDate,
            end_date: moment(currentDate)
                .add(+mhdDaysCount - 1, 'days')
                .format(),
            days_count: `${mhdDaysCount}`,
        };

        const { updatedEntities, connections } = createWaterfall(
            charterPositions,
            currentDate,
            positionsEntities,
            settings,
            lastLaborPosition.guid,
            plannedMhd,
            remainder,
            long
        );

        charterPositions
            .map((position) => updatedEntities[position.guid])
            .filter((position) => position.planned.start_date)
            .forEach((position) => {
                queue.push({
                    data: {
                        data: {
                            guid: position.guid,
                            startDate: moment(position.planned.start_date).format('YYYY-MM-DD'),
                            endDate: moment(position.planned.end_date).format('YYYY-MM-DD'),
                        },
                        type: 'update',
                        action: 'create_group',
                    },
                    subEntityType: 'step',
                    subEntityId: parentStage.id,
                    t: Date.now(),
                });
            });

        const queueConnections = [
            {
                data: {
                    data: {
                        connection: connections,
                    },
                    type: 'save_connections',
                    action: 'create_group',
                },
                subEntityType: 'step',
                subEntityId: parentStage.id,
                t: Date.now(),
            },
        ];

        const newConnections = connections.map((connection) => ({
            from: connection.positionFromGuid,
            to: connection.positionToGuid,
            type: connection.connectionType,
        }));

        queueFetch(queue);
        queueFetch(queueConnections);

        updateAll(updatedEntities);
        dispatch(positionsConnectionsActions.addConnections(newConnections));
    };

    const increaseCharter = (charter, charterForm) => {
        let updatedPositionsEntities = { ...allPositions };
        const queue = [];
        const charterPositions = charter.children.map((guid) => allPositions[guid]);
        const charterLabor = charterPositions.filter((position) => position.type === 'labor');
        const charterMHD = charterPositions.filter((position) => position.type !== 'labor');

        const charterLaborNotNull = charterLabor.filter((position) => position.planned.end_date);

        const lastPositionGuid = charterLaborNotNull.length
            ? lastLaborPositionGuid
            : charterMHD.slice(-1)[0].guid;

        const lastPosition = allPositions[lastPositionGuid];

        const prevPositionsType1 = lastPosition.connections
            .filter((connection) => connection.type === 1 && connection.to === lastPosition.guid)
            .map((connection) => allPositions[connection.from])
            .sort(
                (positionA, positionB) =>
                    moment(positionA.planned.start_date).valueOf() -
                    moment(positionB.planned.start_date).valueOf()
            );

        let daysCount = charterForm.days;

        let endDate = moment(charterForm.date)
            .add(Number(charterForm.days) - 1, 'days')
            .format();

        if (
            prevPositionsType1.length &&
            moment(endDate).add(1, 'days').isAfter(moment(prevPositionsType1[0].planned.start_date))
        ) {
            endDate = moment(prevPositionsType1[0].planned.start_date).add(-1, 'days').format();
            daysCount = `${Math.abs(
                moment(charter.planned.start_date).diff(moment(endDate), 'days') - 1
            )}`;
        }

        const start = moment(allPositions[lastPositionGuid].planned.start_date);
        const end = moment(endDate);
        const duration = moment.duration(end.diff(start));
        const days = `${Math.round(+duration.asDays()) + 1}`;

        let planned = {
            ...allPositions[lastPositionGuid].planned,
            days_count: days,
            end_date: endDate,
        };

        const updatedPosition = { ...allPositions[lastPositionGuid], planned };

        queue.push({
            data: {
                data: {
                    guid: updatedPosition.guid,
                    startDate: moment(updatedPosition.planned.start_date).format('YYYY-MM-DD'),
                    endDate: moment(updatedPosition.planned.end_date).format('YYYY-MM-DD'),
                },
                type: 'update',
                action: 'increase_group',
            },
            subEntityType: 'step',
            subEntityId: parentStage.id,
            t: Date.now(),
        });

        const moveDays = +daysCount - +charter.planned.days_count;

        const nextPositions = updatedPosition.connections
            .filter(
                (connection) => connection.type === 0 && updatedPosition.guid === connection.from
            )
            .map((connection) => allPositions[connection.to]);

        const chainsConnections = nextPositions.map((position) => [
            position,
            ...getChainConnection(position, allPositions),
        ]);

        if (chainsConnections.length) {
            chainsConnections.forEach((chain) => {
                const nextPosition = chain[0];

                const initEndDate = allPositions[lastPositionGuid].planned.end_date;

                const nextPositionStartDate = nextPosition.planned.start_date;

                const diffBeforePosition =
                    moment(nextPositionStartDate).diff(moment(initEndDate), 'days') - 1;

                if (diffBeforePosition - moveDays < 0) {
                    const diff = Math.abs(diffBeforePosition - moveDays);

                    const updatePosition = getUpdatePositions(chain, diff);

                    updatePosition.forEach((position) => {
                        queue.push({
                            data: {
                                data: {
                                    guid: position.changes.guid,
                                    startDate: moment(position.changes.planned.start_date).format(
                                        'YYYY-MM-DD'
                                    ),
                                    endDate: moment(position.changes.planned.end_date).format(
                                        'YYYY-MM-DD'
                                    ),
                                },
                                type: 'update',
                                action: 'increase_group',
                            },
                            subEntityType: 'step',
                            subEntityId: parentStage.id,
                            t: Date.now(),
                        });
                    });

                    updatedPositionsEntities[updatedPosition.guid] = updatedPosition;

                    updatePosition.forEach((item) => {
                        updatedPositionsEntities[item.id] = item.changes;
                    });
                }
            });
        }

        const updatePositionEntity = {
            [updatedPosition.guid]: updatedPosition,
        };

        queueFetch(queue);

        updateAll({ ...updatedPositionsEntities, ...updatePositionEntity });
    };

    const decreaseCharter = (charter, difference) => {
        const charterPositions = charter.children.map((guid) => allPositions[guid]);
        const charterLabor = charterPositions.filter((position) => position.type === 'labor');
        // const charterMHD = charterPositions.filter((position) => position.type !== 'labor');

        const queue = [];

        let positionWithConnection = charterPositions.filter(
            (position) => position.connections.length
        );

        const positionWithoutConnection = charterPositions.filter(
            (position) => !position.connections.length
        );

        const chains = getChains(
            positionWithConnection,
            positionWithConnection.slice(-1)[0],
            allPositions
        )
            .filter((chain) => chain.length > 1)
            .map((chain, index) => ({
                [index]: { chain, length: chain.length },
            }))
            .sort(
                (chainA, chainB) =>
                    Object.values(chainB)[0].length - Object.values(chainA)[0].length
            );

        let differenceRemainder = difference;

        const isConnection = !!chains.length;

        if (!isConnection) {
            let resultPositionNotConnection = {};

            charter.children.forEach((guid) => {
                resultPositionNotConnection[guid] = allPositions[guid];
            });

            let planned = charter.planned;

            const days_count = +charter.planned.days_count - difference;

            if (days_count === 1) {
                planned = {
                    days_count: '1',
                    start_date: charter.planned.start_date,
                    end_date: charter.planned.start_date,
                };

                charter.children
                    .filter((guid) => allPositions[guid]?.planned.days_count)
                    .forEach((guid) => {
                        resultPositionNotConnection[guid] = {
                            ...resultPositionNotConnection[guid],
                            planned,
                        };
                    });
            } else {
                planned = {
                    ...planned,
                    days_count: `${days_count}`,
                    end_date: moment(planned.start_date)
                        .add(days_count - 1, 'days')
                        .format(),
                };

                charter.children.forEach((guid) => {
                    const currentPosition = resultPositionNotConnection[guid];

                    if (!currentPosition.planned.start_date) {
                        return;
                    }

                    let endDatePosition = currentPosition.planned.end_date;
                    let startDatePosition = currentPosition.planned.start_date;
                    const endDateCharter = planned.end_date;
                    const startDateCharter = planned.start_date;

                    if (moment(endDateCharter).isBefore(moment(endDatePosition))) {
                        const diff = moment(endDateCharter).diff(moment(endDatePosition), 'days');

                        endDatePosition = moment(currentPosition.planned.end_date)
                            .add(-Math.abs(diff), 'days')
                            .format();

                        startDatePosition = moment(currentPosition.planned.start_date)
                            .add(-Math.abs(diff), 'days')
                            .format();

                        let positionPlanned;

                        if (moment(startDatePosition).isBefore(moment(startDateCharter))) {
                            positionPlanned = {
                                ...planned,
                            };
                        } else {
                            positionPlanned = {
                                ...currentPosition.planned,
                                start_date: startDatePosition,
                                end_date: endDatePosition,
                            };
                        }

                        resultPositionNotConnection[guid] = {
                            ...currentPosition,
                            planned: positionPlanned,
                        };
                    }
                });

                const updateManyPositions = Object.values(resultPositionNotConnection).map(
                    (position) => ({
                        id: position.guid,
                        changes: position,
                    })
                );

                dispatch(positionActions.updateMany(updateManyPositions));

                dispatch(
                    charterActions.updateOne({
                        id: charter.guid,
                        changes: { ...charter, planned },
                    })
                );
            }

            Object.values(resultPositionNotConnection)
                .filter((position) => position.planned.start_date)
                .forEach((position) => {
                    queue.push({
                        data: {
                            data: {
                                guid: position.guid,
                                startDate: moment(position.planned.start_date).format('YYYY-MM-DD'),
                                endDate: moment(position.planned.end_date).format('YYYY-MM-DD'),
                            },
                            type: 'update',
                            action: 'decrease_group',
                        },
                        subEntityType: 'step',
                        subEntityId: parentStage.id,
                        t: Date.now(),
                    });
                });

            updateAll(resultPositionNotConnection);

            queueFetch(queue);
            return;
        }
        const longestChain = Object.values(chains[0])[0];

        differenceRemainder =
            +charter.planned.days_count - difference < longestChain.length
                ? difference - (longestChain.length - (+charter.planned.days_count - difference))
                : difference;

        let daysCount = +charter.planned.days_count - difference;

        if (daysCount < longestChain.length) {
            dispatch(gantActions.changeShowPopup(true));
        }

        let resultPositions = {};

        [...positionWithConnection, ...positionWithoutConnection].forEach((position) => {
            resultPositions[position.guid] = position;
        });

        chains.forEach((chain) => {
            const filteredChain = Object.values(chain)[0].chain.filter((position, index) => {
                if (index === 0 && !resultPositions[position.guid]) {
                    return false;
                }
                return true;
            });
            resultPositions = {
                ...resultPositions,
                ...updateChain(
                    filteredChain,
                    resultPositions,
                    charter.planned,
                    differenceRemainder,
                    allPositions,
                    updateConnectionPositions
                ),
            };
        });

        positionWithoutConnection.forEach((position) => {
            resultPositions = {
                ...resultPositions,
                ...updatePositionWithoutConnect(
                    position,
                    resultPositions,
                    charter.planned,
                    differenceRemainder,
                    allPositions
                ),
            };
        });

        charterPositions
            .map((position) => resultPositions[position.guid])
            .filter((position) => position && position.planned.start_date)
            .forEach((position) => {
                queue.push({
                    data: {
                        data: {
                            guid: position.guid,
                            startDate: moment(position.planned.start_date).format('YYYY-MM-DD'),
                            endDate: moment(position.planned.end_date).format('YYYY-MM-DD'),
                        },
                        type: 'update',
                        action: 'decrease_group',
                    },
                    subEntityType: 'step',
                    subEntityId: parentStage.id,
                    t: Date.now(),
                });
            });

        queueFetch(queue);
        updateAll(resultPositions);
    };

    const increaseCharterNew = (charterForm, difference, positionsEntities) => {
        let updatedPositionsEntities = positionsEntities;

        const positions = { ...allPositions, ...updatedPositionsEntities };

        const charterPositions = charter.children.map((guid) => positions[guid]);
        const charterLabor = charterPositions.filter((position) => position.type === 'labor');
        const charterMHD = charterPositions.filter((position) => position.type !== 'labor');

        const charterLaborNotNull = charterLabor.filter((position) => position.planned.end_date);

        const lastPositionGuid = charterLaborNotNull.length
            ? lastLaborPositionGuid
            : charterMHD.slice(-1)[0].guid;

        const endDate = moment(charterForm.date)
            .add(Number(charterForm.days) - 1, 'days')
            .format();

        const start = moment(positions[lastPositionGuid].planned.start_date);
        const end = moment(endDate);
        const duration = moment.duration(end.diff(start));
        const days = `${Math.round(+duration.asDays()) + 1}`;

        const days_count = +positions[lastPositionGuid].planned.days_count + difference;

        const planned = {
            ...positions[lastPositionGuid].planned,
            days_count: `${days_count}`,
            end_date: moment(positions[lastPositionGuid].planned.start_date)
                .add(days_count - 1, 'days')
                .format(),
        };

        const updatedPosition = { ...positions[lastPositionGuid], planned };

        const moveDays = difference;

        const nextPositions = updatedPosition.connections
            .filter(
                (connection) => connection.type === 0 && updatedPosition.guid === connection.from
            )
            .map((connection) => allPositions[connection.to]);

        const chainsConnections = nextPositions.map((position) => [
            position,
            ...getChainConnection(position, allPositions),
        ]);

        if (chainsConnections.length) {
            chainsConnections.forEach((chain) => {
                const nextPosition = chain[0];

                const initEndDate = positions[lastPositionGuid].planned.end_date;

                const nextPositionStartDate = nextPosition.planned.start_date;

                const diffBeforePosition =
                    moment(nextPositionStartDate).diff(moment(initEndDate), 'days') - 1;

                if (diffBeforePosition - moveDays < 0) {
                    const diff = Math.abs(diffBeforePosition - moveDays);

                    const updatePosition = getUpdatePositions(chain, diff);

                    updatedPositionsEntities[updatedPosition.guid] = updatedPosition;

                    updatePosition.forEach((item) => {
                        updatedPositionsEntities[item.id] = item.changes;
                    });
                }
            });
        }

        const updatePositionEntity = {
            [updatedPosition.guid]: updatedPosition,
        };

        return { ...updatedPositionsEntities, ...updatePositionEntity };

        // updateAll({ ...updatedPositionsEntities, ...updatePositionEntity });
    };

    const decreaseCharterNew = (difference, positionsEntities) => {
        let updatedPositionsEntities = positionsEntities;

        const positions = { ...allPositions, ...updatedPositionsEntities };

        const charterPositions = charter.children.map((guid) => positions[guid]);

        const charterLabor = charterPositions.filter((position) => position.type === 'labor');
        const charterMHD = charterPositions.filter((position) => position.type !== 'labor');

        let positionWithConnection = charterPositions.filter(
            (position) => position.connections.length
        );

        const positionWithoutConnection = charterPositions.filter(
            (position) => !position.connections.length
        );

        const chains = getChains(
            positionWithConnection,
            positionWithConnection.slice(-1)[0],
            positions
        )
            .filter((chain) => chain.length > 1)
            .map((chain, index) => ({
                [index]: { chain, length: chain.length },
            }))
            .sort(
                (chainA, chainB) =>
                    Object.values(chainB)[0].length - Object.values(chainA)[0].length
            );

        let differenceRemainder = Math.abs(difference);

        const isConnection = !!chains.length;

        if (!isConnection) {
            let resultPositionNotConnection = {};

            charter.children.forEach((guid) => {
                resultPositionNotConnection[guid] = positions[guid];
            });

            let planned = charter.planned;

            const days_count = +charter.planned.days_count - Math.abs(difference);

            if (days_count === 1) {
                planned = {
                    days_count: '1',
                    start_date: charter.planned.start_date,
                    end_date: charter.planned.start_date,
                };

                charter.children
                    .filter((guid) => positions[guid]?.planned.days_count)
                    .forEach((guid) => {
                        resultPositionNotConnection[guid] = {
                            ...resultPositionNotConnection[guid],
                            planned,
                        };
                    });
            } else {
                planned = {
                    ...planned,
                    days_count: `${days_count}`,
                    end_date: moment(planned.start_date)
                        .add(days_count - 1, 'days')
                        .format(),
                };

                charter.children.forEach((guid) => {
                    const currentPosition = resultPositionNotConnection[guid];

                    if (!currentPosition.planned.start_date) {
                        return updatedPositionsEntities;
                    }

                    let endDatePosition = currentPosition.planned.end_date;
                    let startDatePosition = currentPosition.planned.start_date;
                    const endDateCharter = planned.end_date;
                    const startDateCharter = planned.start_date;

                    if (moment(endDateCharter).isBefore(moment(endDatePosition))) {
                        const diff = moment(endDateCharter).diff(moment(endDatePosition), 'days');

                        endDatePosition = moment(currentPosition.planned.end_date)
                            .add(-Math.abs(diff), 'days')
                            .format();

                        startDatePosition = moment(currentPosition.planned.start_date)
                            .add(-Math.abs(diff), 'days')
                            .format();

                        let positionPlanned;

                        if (moment(startDatePosition).isBefore(moment(startDateCharter))) {
                            positionPlanned = {
                                ...planned,
                            };
                        } else {
                            positionPlanned = {
                                ...currentPosition.planned,
                                start_date: startDatePosition,
                                end_date: endDatePosition,
                            };
                        }

                        resultPositionNotConnection[guid] = {
                            ...currentPosition,
                            planned: positionPlanned,
                        };
                    }
                });

                const updateManyPositions = Object.values(resultPositionNotConnection).map(
                    (position) => ({
                        id: position.guid,
                        changes: position,
                    })
                );

                // dispatch(positionActions.updateMany(updateManyPositions));

                dispatch(
                    charterActions.updateOne({
                        id: charter.guid,
                        changes: { ...charter, planned },
                    })
                );
            }

            // updateAll(resultPositionNotConnection);

            return { ...updatedPositionsEntities, ...resultPositionNotConnection };
        }

        const longestChain = Object.values(chains[0])[0];

        differenceRemainder =
            +charter.planned.days_count - difference < longestChain.length
                ? Math.abs(difference) -
                  (longestChain.length - (+charter.planned.days_count - Math.abs(difference)))
                : Math.abs(difference);

        let daysCount = +charter.planned.days_count - Math.abs(difference);

        if (daysCount < longestChain.length) {
            dispatch(gantActions.changeShowPopup(true));
        }

        let resultPositions = {};

        [...positionWithConnection, ...positionWithoutConnection].forEach((position) => {
            resultPositions[position.guid] = position;
        });

        const sortedPositions = charterPositions
            .filter((position) => position.planned.start_date)
            .sort(
                (positionA, positionB) =>
                    moment(positionA.planned.end_date).valueOf() -
                    moment(positionB.planned.end_date).valueOf()
            );

        const start_date = sortedPositions[0].planned.start_date;
        const end_date = sortedPositions.slice(-1)[0].planned.end_date;
        const days_count = getCountDaysFromDate(start_date, end_date);
        const updatedPlanned = {
            start_date,
            end_date,
            days_count: String(days_count),
        };

        chains.forEach((chain) => {
            const filteredChain = Object.values(chain)[0].chain.filter((position, index) => {
                if (index === 0 && !resultPositions[position.guid]) {
                    return false;
                }
                return true;
            });
            resultPositions = {
                ...resultPositions,
                ...updateChain(
                    filteredChain,
                    resultPositions,
                    updatedPlanned,
                    differenceRemainder,
                    positions,
                    updateConnectionPositions
                ),
            };
        });

        positionWithoutConnection.forEach((position) => {
            resultPositions = {
                ...resultPositions,
                ...updatePositionWithoutConnect(
                    position,
                    resultPositions,
                    updatedPlanned,
                    differenceRemainder,
                    positions
                ),
            };
        });

        return { ...updatedPositionsEntities, ...resultPositions };

        // updateAll(resultPositions);
    };

    const moveCharterNew = (moveDays, positionsEntities) => {
        const updatedPositionsEntities = positionsEntities;

        const queue = [];
        if (!positionsWithConnection.length) {
            charterPositions.forEach((position) => {
                const planned = {
                    ...position.planned,
                    start_date: moment(position.planned.start_date).add(moveDays, 'days').format(),
                    end_date: moment(position.planned.end_date).add(moveDays, 'days').format(),
                };

                if (position.planned.start_date) {
                    updatedPositionsEntities[position.guid] = { ...position, planned };

                    queue.push({
                        data: {
                            data: {
                                guid: position.guid,
                                startDate: moment(planned.start_date).format('YYYY-MM-DD'),
                                endDate: moment(planned.end_date).format('YYYY-MM-DD'),
                            },
                            type: 'update',
                            action: 'move_group',
                        },
                        subEntityType: 'step',
                        subEntityId: parentStage.id,
                        t: Date.now(),
                    });
                }
            });
            queueFetch(queue);
            return updatedPositionsEntities;
        }

        const chainsPositionFromOtherItem = getConnectionsFromOtherItem(
            charterPositions,
            allPositions
        );

        if (moveDays > 0) {
            const firstPositionWithPrev = charterPositions
                .sort(
                    (positionA, positionB) =>
                        moment(positionB.planned.end_date).valueOf() -
                        moment(positionA.planned.end_date).valueOf()
                )
                .find((position) => {
                    let result = false;
                    position.connections.forEach((connection) => {
                        if (
                            connection.type === 1 &&
                            connection.to === position.guid &&
                            !charter.children.includes(connection.from)
                        ) {
                            result = true;
                        }
                    });
                    return result;
                });

            const prevPositionConnection = firstPositionWithPrev?.connections.find(
                (connection) =>
                    connection.to === firstPositionWithPrev?.guid &&
                    connection.type === 1 &&
                    !charter.children.includes(connection.from)
            );

            if (!prevPositionConnection) {
                charterPositions.forEach((position) => {
                    const planned = {
                        ...position.planned,
                        start_date: moment(position.planned.start_date)
                            .add(moveDays, 'days')
                            .format(),
                        end_date: moment(position.planned.end_date).add(moveDays, 'days').format(),
                    };
                    if (position.planned.start_date) {
                        updatedPositionsEntities[position.guid] = { ...position, planned };
                    }
                });

                if (chainsPositionFromOtherItem.length) {
                    chainsPositionFromOtherItem.forEach((chainsConnections) => {
                        chainsConnections.forEach((chain) => {
                            const firstChainPosition = chain[0];
                            const connectionWithCurrentPosition =
                                firstChainPosition.connections.filter(
                                    (connection) =>
                                        connection.to === firstChainPosition.guid &&
                                        charter.children.includes(connection.from)
                                )[0];

                            if (connectionWithCurrentPosition.type === 1) {
                                const updatePosition = getUpdatePositions(chain, moveDays);
                                updatePosition.forEach((position) => {
                                    queue.push({
                                        data: {
                                            data: {
                                                guid: position.changes.guid,
                                                startDate: moment(
                                                    position.changes.planned.start_date
                                                ).format('YYYY-MM-DD'),
                                                endDate: moment(
                                                    position.changes.planned.end_date
                                                ).format('YYYY-MM-DD'),
                                            },
                                            type: 'update',
                                            action: 'move_group',
                                        },
                                        subEntityType: 'step',
                                        subEntityId: parentStage.id,
                                        t: Date.now(),
                                    });
                                });
                                updatePosition.forEach((item) => {
                                    updatedPositionsEntities[item.id] = item.changes;
                                });
                            } else {
                                const initEndDate =
                                    allPositions[connectionWithCurrentPosition.from].planned
                                        .end_date;

                                const nextPositionStartDate = firstChainPosition.planned.start_date;

                                const diffBeforePosition =
                                    moment(nextPositionStartDate).diff(
                                        moment(initEndDate),
                                        'days'
                                    ) - 1;

                                if (diffBeforePosition - moveDays < 0) {
                                    const diff = Math.abs(diffBeforePosition - moveDays);

                                    const updatePosition = getUpdatePositions(chain, diff);
                                    updatePosition.forEach((position) => {
                                        queue.push({
                                            data: {
                                                data: {
                                                    guid: position.changes.guid,
                                                    startDate: moment(
                                                        position.changes.planned.start_date
                                                    ).format('YYYY-MM-DD'),
                                                    endDate: moment(
                                                        position.changes.planned.end_date
                                                    ).format('YYYY-MM-DD'),
                                                },
                                                type: 'update',
                                                action: 'move_group',
                                            },
                                            subEntityType: 'step',
                                            subEntityId: parentStage.id,
                                            t: Date.now(),
                                        });
                                    });

                                    updatePosition.forEach((item) => {
                                        updatedPositionsEntities[item.id] = item.changes;
                                    });
                                }
                            }
                        });
                    });
                }
            } else {
                const prevPosition = allPositions[prevPositionConnection.from];

                let diffBeforeConnectPosition =
                    moment(prevPosition.planned.start_date).diff(
                        moment(firstPositionWithPrev.planned.end_date),
                        'days'
                    ) - 1;

                const newMoveDays =
                    diffBeforeConnectPosition - moveDays < 0 ? diffBeforeConnectPosition : moveDays;

                charterPositions.forEach((position) => {
                    const planned = {
                        ...position.planned,
                        start_date: moment(position.planned.start_date)
                            .add(newMoveDays, 'days')
                            .format(),
                        end_date: moment(position.planned.end_date)
                            .add(newMoveDays, 'days')
                            .format(),
                    };
                    if (position.planned.start_date) {
                        updatedPositionsEntities[position.guid] = { ...position, planned };
                    }
                });

                if (chainsPositionFromOtherItem.length) {
                    chainsPositionFromOtherItem.forEach((chainsConnections) => {
                        chainsConnections.forEach((chain) => {
                            const firstChainPosition = chain[0];
                            const connectionWithCurrentPosition =
                                firstChainPosition.connections.filter(
                                    (connection) =>
                                        connection.to === firstChainPosition.guid &&
                                        charter.children.includes(connection.from)
                                )[0];

                            if (connectionWithCurrentPosition.type === 1) {
                                const updatePosition = getUpdatePositions(chain, newMoveDays);

                                updatePosition.forEach((position) => {
                                    queue.push({
                                        data: {
                                            data: {
                                                guid: position.changes.guid,
                                                startDate: moment(
                                                    position.changes.planned.start_date
                                                ).format('YYYY-MM-DD'),
                                                endDate: moment(
                                                    position.changes.planned.end_date
                                                ).format('YYYY-MM-DD'),
                                            },
                                            type: 'update',
                                            action: 'move_group',
                                        },
                                        subEntityType: 'step',
                                        subEntityId: parentStage.id,
                                        t: Date.now(),
                                    });
                                });
                                updatePosition.forEach((item) => {
                                    updatedPositionsEntities[item.id] = item.changes;
                                });
                            } else {
                                const initEndDate =
                                    allPositions[connectionWithCurrentPosition.from].planned
                                        .end_date;

                                const nextPositionStartDate = firstChainPosition.planned.start_date;

                                const diffBeforePosition =
                                    moment(nextPositionStartDate).diff(
                                        moment(initEndDate),
                                        'days'
                                    ) - 1;

                                if (diffBeforePosition - newMoveDays < 0) {
                                    const diff = Math.abs(diffBeforePosition - newMoveDays);

                                    const updatePosition = getUpdatePositions(chain, diff);

                                    updatePosition.forEach((position) => {
                                        queue.push({
                                            data: {
                                                data: {
                                                    guid: position.changes.guid,
                                                    startDate: moment(
                                                        position.changes.planned.start_date
                                                    ).format('YYYY-MM-DD'),
                                                    endDate: moment(
                                                        position.changes.planned.end_date
                                                    ).format('YYYY-MM-DD'),
                                                },
                                                type: 'update',
                                                action: 'move_group',
                                            },
                                            subEntityType: 'step',
                                            subEntityId: parentStage.id,
                                            t: Date.now(),
                                        });
                                    });

                                    updatePosition.forEach((item) => {
                                        updatedPositionsEntities[item.id] = item.changes;
                                    });
                                }
                            }
                        });
                    });
                }
            }
        } else {
            const firstPositionWithPrev = charterPositions
                .sort(
                    (positionA, positionB) =>
                        moment(positionA.planned.start_date).valueOf() -
                        moment(positionB.planned.start_date).valueOf()
                )
                .find((position) => {
                    let result = false;
                    position.connections.forEach((connection) => {
                        if (
                            connection.type === 0 &&
                            connection.to === position.guid &&
                            !charter.children.includes(connection.from)
                        ) {
                            result = true;
                        }
                    });
                    return result;
                });

            const prevPositionConnection = firstPositionWithPrev?.connections.find(
                (connection) =>
                    connection.to === firstPositionWithPrev?.guid &&
                    connection.type === 0 &&
                    !charter.children.includes(connection.from)
            );
            if (!prevPositionConnection) {
                charterPositions.forEach((position) => {
                    const planned = {
                        ...position.planned,
                        start_date: moment(position.planned.start_date)
                            .add(moveDays, 'days')
                            .format(),
                        end_date: moment(position.planned.end_date).add(moveDays, 'days').format(),
                    };
                    if (position.planned.start_date) {
                        updatedPositionsEntities[position.guid] = { ...position, planned };
                    }
                });

                if (chainsPositionFromOtherItem.length) {
                    chainsPositionFromOtherItem.forEach((chainsConnections) => {
                        chainsConnections.forEach((chain) => {
                            const firstChainPosition = chain[0];
                            const connectionWithCurrentPosition =
                                firstChainPosition.connections.filter(
                                    (connection) =>
                                        connection.to === firstChainPosition.guid &&
                                        charter.children.includes(connection.from)
                                )[0];

                            if (connectionWithCurrentPosition.type === 1) {
                                const updatePosition = getUpdatePositions(chain, moveDays);

                                updatePosition.forEach((position) => {
                                    queue.push({
                                        data: {
                                            data: {
                                                guid: position.changes.guid,
                                                startDate: moment(
                                                    position.changes.planned.start_date
                                                ).format('YYYY-MM-DD'),
                                                endDate: moment(
                                                    position.changes.planned.end_date
                                                ).format('YYYY-MM-DD'),
                                            },
                                            type: 'update',
                                            action: 'move_group',
                                        },
                                        subEntityType: 'step',
                                        subEntityId: parentStage.id,
                                        t: Date.now(),
                                    });
                                });
                                updatePosition.forEach((item) => {
                                    updatedPositionsEntities[item.id] = item.changes;
                                });
                            }
                        });
                    });
                }
            } else {
                const prevPosition = allPositions[prevPositionConnection.from];
                let diffBeforeConnectPosition =
                    moment(firstPositionWithPrev.planned.start_date).diff(
                        moment(prevPosition.planned.end_date),
                        'days'
                    ) - 1;

                const newMoveDays =
                    diffBeforeConnectPosition + moveDays < 0
                        ? -diffBeforeConnectPosition
                        : moveDays;

                charterPositions.forEach((position) => {
                    const planned = {
                        ...position.planned,
                        start_date: moment(position.planned.start_date)
                            .add(newMoveDays, 'days')
                            .format(),
                        end_date: moment(position.planned.end_date)
                            .add(newMoveDays, 'days')
                            .format(),
                    };
                    if (position.planned.start_date) {
                        updatedPositionsEntities[position.guid] = { ...position, planned };
                    }
                });

                if (chainsPositionFromOtherItem.length) {
                    chainsPositionFromOtherItem.forEach((chainsConnections) => {
                        chainsConnections.forEach((chain) => {
                            const firstChainPosition = chain[0];
                            const connectionWithCurrentPosition =
                                firstChainPosition.connections.filter(
                                    (connection) =>
                                        connection.to === firstChainPosition.guid &&
                                        charter.children.includes(connection.from)
                                )[0];

                            if (connectionWithCurrentPosition.type === 1) {
                                const updatePosition = getUpdatePositions(chain, newMoveDays);
                                updatePosition.forEach((item) => {
                                    updatedPositionsEntities[item.id] = item.changes;
                                });
                            }
                        });
                    });
                }
            }
        }

        charterPositions
            .map((position) => updatedPositionsEntities[position.guid])
            .filter((position) => position && position.planned.start_date)
            .forEach((position) => {
                queue.push({
                    data: {
                        data: {
                            guid: position.guid,
                            startDate: moment(position.planned.start_date).format('YYYY-MM-DD'),
                            endDate: moment(position.planned.end_date).format('YYYY-MM-DD'),
                        },
                        type: 'update',
                        action: 'move_group',
                    },
                    subEntityType: 'step',
                    subEntityId: parentStage.id,
                    t: Date.now(),
                });
            });

        queueFetch(queue);

        return updatedPositionsEntities;

        // updateAll(updatedPositionsEntities);
    };

    const onDataPickerCharter = (charter, pickerData) => {
        const charterInitStartDate = charter.planned.start_date;

        const isNeedMove = pickerData.start !== charter.planned.start_date;
        const isNeedResize = pickerData.days !== charter.planned.days_count;

        const moveDays = moment(pickerData.start).diff(moment(charterInitStartDate), 'days');

        let resultUpdating = {};

        if (isNeedMove) {
            const moveResult = moveCharterNew(moveDays, resultUpdating);
            resultUpdating = { ...resultUpdating, ...moveResult };
        }

        if (isNeedResize) {
            const isDecrease = +charter.planned.days_count > +pickerData.days;
            const diff = +pickerData.days - +charter.planned.days_count;

            if (isDecrease) {
                const decreaseResult = decreaseCharterNew(diff, resultUpdating);
                resultUpdating = { ...resultUpdating, ...decreaseResult };
            } else {
                const increaseResult = increaseCharterNew(
                    { date: pickerData.start, days: pickerData.days },
                    diff,
                    resultUpdating
                );
                resultUpdating = { ...resultUpdating, ...increaseResult };
            }
        }

        const queue = [];
        Object.values(resultUpdating)
            .filter((position) => position.planned.start_date)
            .forEach((position) => {
                queue.push({
                    data: {
                        data: {
                            guid: position.guid,
                            startDate: moment(position.planned.start_date).format('YYYY-MM-DD'),
                            endDate: moment(position.planned.end_date).format('YYYY-MM-DD'),
                        },
                        type: 'update',
                        action: 'picker_update_group',
                    },
                    subEntityType: 'step',
                    subEntityId: parentStage.id,
                    t: Date.now(),
                });
            });

        queueFetch(queue);
        updateAll(resultUpdating);
    };

    return {
        resetCharter,
        crateCharter,
        increaseCharter,
        decreaseCharter,
        onHidePopup,
        moveCharterNew,
        onDataPickerCharter,
        updateAll,
    };
};
